import React, { useCallback, useMemo, useState } from 'react';
import { Card, Tab } from 'react-bootstrap';

import {
  GridGetterFunction,
  GridRecord,
} from '../../../common/components/Grid/GridDataTypes';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { SimP1DocumentsSearch } from '../../components/SimP1Documents/SimP1DocumentsSearch';
import { JobConfigInfo } from '../../components/JobConfig/JobConfigInfo';
import { JobTypeEnum } from '../../../common/spdCore/validation/schemas';
import { TabsHistory } from '../../../app/components/TabsHistory';

export const SimP1DocumentsView: React.FC = () => {
  const spd = useSpdCore();
  const [refresh, setRefresh] = useState(false);

  const handleRetry = useCallback(() => setRefresh(r => !r), []);

  const withRetry = useCallback(
    (
      fetch: (
        pageSize: number,
        pageNumber: number,
        orderBy?: string,
        orderDirection?: string,
        filterText?: string,
        filterColumns?: string,
        onRetry?: () => void,
      ) => Promise<GridRecord[]>,
    ): GridGetterFunction =>
      (
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        fetch(
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
          handleRetry,
        ),
    [handleRetry],
  );

  const getAllData = useMemo(
    () => withRetry(spd.judgmentP1Documents.getAllByPortion),
    [spd.judgmentP1Documents, withRetry],
  );
  const getRejectedData = useMemo(
    () => withRetry(spd.judgmentP1Documents.getRejectedByPortion),
    [spd.judgmentP1Documents, withRetry],
  );
  const getIncompleteData = useMemo(
    () => withRetry(spd.judgmentP1Documents.getIncompleteByPortion),
    [spd.judgmentP1Documents, withRetry],
  );
  const getToSignData = useMemo(
    () => withRetry(spd.judgmentP1Documents.getToSignByPortion),
    [spd.judgmentP1Documents, withRetry],
  );
  const getWaitingData = useMemo(
    () => withRetry(spd.judgmentP1Documents.getWaitingByPortion),
    [spd.judgmentP1Documents, withRetry],
  );

  return (
    <Card>
      <Card.Body>
        <TabsHistory
          id="simP1Documents"
          activeKey="incomplete"
          mountOnEnter={true}
          unmountOnExit={true}
        >
          <Tab eventKey="incomplete" title="Niekompletne">
            <SimP1DocumentsSearch
              getData={getIncompleteData}
              getCount={spd.judgmentP1Documents.getIncompleteCount}
              withFilterInColumns={true}
              refresh={refresh}
            />
          </Tab>
          <Tab eventKey="toSign" title="Do podpisu">
            <SimP1DocumentsSearch
              getData={getToSignData}
              getCount={spd.judgmentP1Documents.getToSignCount}
              withFilterInColumns={true}
              refresh={refresh}
            />
          </Tab>
          <Tab eventKey="waiting" title="Oczekujące">
            <SimP1DocumentsSearch
              getData={getWaitingData}
              getCount={spd.judgmentP1Documents.getWaitingCount}
              withFilterInColumns={true}
              refresh={refresh}
            />
            <JobConfigInfo
              items={[
                {
                  jobType: JobTypeEnum.SimP1JudgmentSend,
                  nextStartLabel: 'Następne uruchomienie wysyłki do SIM',
                  frequencyLabel: 'Częstotliwość wysyłki do SIM',
                },
              ]}
            />
          </Tab>
          <Tab eventKey="rejected" title="Odrzucone">
            <SimP1DocumentsSearch
              getData={getRejectedData}
              getCount={spd.judgmentP1Documents.getRejectedCount}
              withFilterInColumns={true}
              refresh={refresh}
            />
          </Tab>
          <Tab eventKey="all" title="Wszystkie">
            <SimP1DocumentsSearch
              getData={getAllData}
              getCount={spd.judgmentP1Documents.getAllCount}
              withFilterInColumns={true}
              refresh={refresh}
            />
          </Tab>
        </TabsHistory>
      </Card.Body>
    </Card>
  );
};
